/**
 * A table of any entity kind, with its columns taken from the registry.
 *
 * Only scalar fields become columns. The listing endpoint deliberately leaves
 * out references and lists - each is a lazy load, so including them would cost
 * a query per row - and a column that is always blank is worse than no column.
 *
 * Fetched WHOLE and filtered in the browser, which is the same shape Model
 * Tree, Flowdown, Parts and Traceability already use. The search box matches
 * any column (see rowFilter) and the grid's own column menus do the faceting,
 * so adding a facet later is a front-end change rather than an API change.
 *
 * ALL THREE filters live in the URL - the box under `search`, the quick filters
 * for enum fields under `quick`, the column conditions under `filter` - so a
 * filtered table is a link, the same property the
 * requirements screen has always had. Navigating to another kind is a new path
 * with no query string, which is what clears them: a search typed on Ports
 * once filtered States and made the page look empty for no stated reason.
 *
 * The sort is in the URL as well (`sort`), and all four are REMEMBERED per path
 * (issue #86): arriving at a kind's bare path puts back the filters last used
 * on THAT kind, never another's - see rememberedFilters.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { DataGrid } from '@mui/x-data-grid'
import type { GridColDef } from '@mui/x-data-grid'
import { ApiError, api } from '../../api'
import { isCostly } from '../../schema'
import type { EntityRow, KindSchema } from '../../schema'
import { MONOSPACE } from '../../theme'
import EmptyState from '../common/EmptyState'
import { naturalComparator, sortsNaturally } from '../common/naturalCompare'
import EntityFilterBar from './EntityFilterBar'
import QuickFilters from './QuickFilters'
import { applyQuick, readQuick, writeQuick } from './quickFilter'
import type { QuickSelection } from './quickFilter'
import { filterRows, readSearch, writeSearch } from './rowFilter'
import { useColumnFilters } from './useColumnFilters'
import { useColumnSort } from './useColumnSort'
import { useRememberedFilters } from './rememberedFilters'
import { PAGE_LIMIT } from '../../api'
import {
  WRAPPING_GRID_PROPS,
  WRAPPING_GRID_SX,
  useFittedHeight,
} from '../common/grid-layout'

export interface EntityTableProps {
  kind: KindSchema
  /** Extra columns a screen wants in front of the generated ones. */
  leading?: GridColDef[]
  /** Where clicking a row goes; defaults to the kind's detail route. */
  hrefFor?: (row: EntityRow) => string
}

export default function EntityTable({ kind, leading = [], hrefFor }: EntityTableProps) {
  const navigate = useNavigate()
  // The same bounded-and-wrapping treatment the requirements table got: this
  // listing is generated from the registry, so it is the one most likely to
  // grow a column past the window. See grid-layout.
  const { ref, height } = useFittedHeight()
  const [params, setParams] = useSearchParams()

  // The search IS the query string, derived rather than copied into state, so
  // there is only one of it and the back button works. The column filters are
  // in the URL too, but through useColumnFilters: the grid also has to keep a
  // condition somebody is half way through building, which the URL will not.
  const search = readSearch(params)
  const { filterModel, setFilterModel } = useColumnFilters()
  const { sortModel, setSortModel } = useColumnSort()
  // Put the last filters back when the list is arrived at bare (issue #86).
  useRememberedFilters()

  /** Replace, not push: typing must not fill up the back button. */
  const setSearch = (next: string) =>
    setParams(writeSearch(params, next), { replace: true })

  const [rows, setRows] = useState<EntityRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * Bumped to ask again after a failure.
   *
   * 'Try again' used to be `setSearch((s) => s)` - a no-op write that worked
   * only because the fetch depended on `search`. When the search stopped
   * reaching the server the effect stopped depending on it, and the button
   * quietly became a button that did nothing: React bails out of a state write
   * that changes nothing, so there was not even a re-render to notice. A
   * counter cannot fail that way, because a new value is the whole point of it.
   */
  const [reloads, setReloads] = useState(0)

  /**
   * Forget the previous kind's rows the moment the kind changes.
   *
   * '/ports' and '/states' are the SAME route - ':plural' - so React keeps this
   * component mounted and its state with it. The fetch below is debounced, so
   * for 250 ms the grid rendered the old kind's rows against the new kind's
   * columns: going from Ports to States showed 'seekerSide' as the only state.
   *
   * Rows only, and the filters need no clearing at all now that they live in
   * the URL: '/ports' and '/states' are different paths, so navigating between
   * them arrives with no query string and both filters read as empty. That is
   * what stops a search typed on Ports from filtering States - the bug this
   * effect was written for - and it now holds without anything resetting it.
   */
  useEffect(() => {
    setRows([])
    setError(null)
  }, [kind.plural])

  /**
   * One request per kind, not one per keystroke.
   *
   * The search no longer reaches the server, so the debounce that used to sit
   * here has nothing to protect: `search` is not a dependency, and typing costs
   * nothing. That is the point of the change - a facet added later is a column
   * and a filter, with no round trip and no API parameter to add.
   *
   * `reloads` is the other way in, for 'Try again' after a failure.
   */
  useEffect(() => {
    let live = true
    setLoading(true)
    api
      .list(kind.plural, { limit: PAGE_LIMIT })
      .then((page) => {
        if (live) {
          // A verification usage is an inclusion (issue #113), listed under the
          // plan or campaign that holds it; here it would repeat its definition
          // (issue #117).
          const items =
            kind.name === 'verification'
              ? page.items.filter((row) => row.is_definition !== false)
              : page.items
          setRows(items)
          // Hidden rows are not "cut off", so they leave the total as well.
          setTotal(page.total - (page.items.length - items.length))
          setError(null)
        }
      })
      .catch((failure: ApiError) => live && setError(failure))
      .finally(() => live && setLoading(false))

    return () => {
      live = false
    }
  }, [kind.plural, reloads])

  // Quick filters (issue #68) for the kind's ENUM fields, the few columns a
  // table is sliced by - the same two-at-once question the requirements screen
  // asks, for the same reason: the grid holds one column condition. Search
  // first, then these, then the grid. Hidden for an enum with fewer than two
  // values present, so a kind with nothing to slice by shows nothing new.
  const quick = useMemo(() => readQuick(params), [params])
  const setQuick = (next: QuickSelection) =>
    setParams((now) => writeQuick(now, next), { replace: true })
  const quickFields = useMemo(
    () =>
      kind.fields
        .filter((field) => field.kind === 'enum')
        .map((field) => ({ name: field.name, label: field.label })),
    [kind.fields],
  )

  const searched = useMemo(() => filterRows(rows, search), [rows, search])
  const shown = useMemo(() => applyQuick(searched, quick), [searched, quick])

  /**
   * Whether the fetch itself was capped - NOT whether the filter matched
   * everything.
   *
   * The distinction is the whole reason this is here: below the cap, a search
   * that finds nothing really means the model has nothing, because every row is
   * in the browser. Above it, a search that finds nothing means nothing among
   * the first PAGE_LIMIT, and no amount of narrowing will reach the rest.
   */
  const truncated = !loading && total > rows.length

  const columns = useMemo<GridColDef[]>(() => {
    const generated: GridColDef[] = kind.fields
      // A DERIVED reference is the exception: it is where the declaration
      // already sits - the part a port is inside - so the listing eager-loads
      // it for the whole page (`listed_references` on the server) and it costs
      // nothing. Dropped with the other references, the Ports table was a list
      // of port names on no part (issue #52).
      .filter((field) => (!isCostly(field) || (field.kind === 'reference' && field.derived))
        && field.name !== 'identifier')
      .map((field) => ({
        field: field.name,
        headerName: field.label,
        flex: field.kind === 'doc' ? 3 : 1,
        minWidth: field.kind === 'doc' ? 260 : 110,
        // A boolean renders as a word rather than 'true'/'false'.
        ...(field.kind === 'flag' || field.kind === 'conjugate'
          ? { type: 'boolean' as const, minWidth: 90, flex: 0 }
          : {}),
        // Ids, names and lists of ids sort NATURALLY, so SUB_2 comes before
        // SUB_10 (issue #82). Prose, choices, numbers and flags keep the
        // grid's own comparator.
        ...(sortsNaturally(field.kind) ? { sortComparator: naturalComparator } : {}),
      }))

    return [
      {
        field: 'id',
        headerName: 'ID',
        minWidth: 150,
        sortComparator: naturalComparator,
        renderCell: (params) => (
          <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>{params.value as string}</Box>
        ),
      },
      ...leading,
      ...generated,
    ]
  }, [kind.fields, leading])

  if (error) {
    return (
      <EmptyState
        title="Could not load"
        description={error.message}
        actions={[{ label: 'Try again', onClick: () => setReloads((n) => n + 1) }]}
      />
    )
  }

  return (
    <Paper sx={{ p: 1.5 }}>
      <Box sx={{ mb: 1.5 }}>
        <EntityFilterBar plural={kind.plural} value={search} onChange={setSearch} />
      </Box>
      {/* Margin only when the row drew something: it draws nothing for a kind
          with no enum worth slicing by. */}
      <Box sx={{ '&:not(:empty)': { mb: 1.5 } }}>
        <QuickFilters
          rows={rows}
          counted={searched}
          fields={quickFields}
          value={quick}
          onChange={setQuick}
        />
      </Box>
      {/* Said rather than implied: a table holding the first 5,000 of 6,000
          looks exactly like a table holding everything, and once the fetch is
          capped, narrowing the filter cannot reach what was never fetched. */}
      {truncated && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          Showing the first {rows.length} of {total}. The filter searches only these.
        </Typography>
      )}
      <Box ref={ref} sx={{ height }}>
        <DataGrid
          {...WRAPPING_GRID_PROPS}
          rows={shown}
          columns={columns}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          // CONTROLLED rather than held inside the grid, so a column-filtered
          // table is a link. Uncontrolled, the grid keeps the model in its own
          // state, where nothing can read it and a reload loses it - which is
          // how the five dropdowns' linkability would have been quietly
          // dropped when they became column filters. See useColumnFilters for
          // why the model is not simply the URL's.
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          // In the URL for the same reason, and remembered with the filters.
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={(params) =>
            navigate(
              hrefFor
                ? hrefFor(params.row as EntityRow)
                : `/${kind.plural}/${encodeURIComponent(String(params.id))}`,
            )
          }
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ ...WRAPPING_GRID_SX, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>
    </Paper>
  )
}
